import { Injectable, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CardActivityEntity } from 'src/app/models/entities/card-activity.entity';
import { TenantService } from 'src/app/services/tenant.service';
import { UserService } from './user.service';

interface OpenSession {
  cardKey: string;
  cardName: string;
  boardId: string;
  openedAt: number;
}

const MIN_SESSION_SECONDS = 5;

@Injectable({ providedIn: 'root' })
export class CardActivityService implements OnDestroy {
  private tenantId: string = '';
  private dbPath: string = '';
  private openSessions: Map<string, OpenSession> = new Map();
  private boundUnload = () => this.flushAll();

  constructor(
    private afs: AngularFirestore,
    private tenantService: TenantService,
    private userService: UserService
  ) {
    this.tenantService.tenantId$.subscribe(tenantId => {
      if (tenantId) {
        this.tenantId = tenantId;
        this.dbPath = `${tenantId}/data/cardActivity`;
      }
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.boundUnload);
    }
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.boundUnload);
    }
  }

  public startSession(cardKey: string, boardId: string, cardName: string): void {
    if (!cardKey) return;
    if (this.openSessions.has(cardKey)) return;

    this.openSessions.set(cardKey, {
      cardKey,
      cardName,
      boardId,
      openedAt: Date.now()
    });
  }

  public endSession(cardKey: string): void {
    const session = this.openSessions.get(cardKey);
    if (!session) return;
    this.openSessions.delete(cardKey);

    const closedAt = Date.now();
    const durationSec = Math.round((closedAt - session.openedAt) / 1000);
    if (durationSec < MIN_SESSION_SECONDS) return;

    this.persistSession(session, closedAt, durationSec);
  }

  private flushAll(): void {
    const closedAt = Date.now();
    this.openSessions.forEach(session => {
      const durationSec = Math.round((closedAt - session.openedAt) / 1000);
      if (durationSec >= MIN_SESSION_SECONDS) {
        this.persistSession(session, closedAt, durationSec);
      }
    });
    this.openSessions.clear();
  }

  private persistSession(session: OpenSession, closedAt: number, durationSec: number): void {
    const user = this.userService.currentUser;
    if (!user || !user.key || !this.tenantId) return;

    const dayKey = this.dayKeyFromTimestamp(session.openedAt);
    const docId = `${user.key}_${session.cardKey}_${dayKey}`;
    const docRef = this.afs.doc(`${this.dbPath}/${docId}`).ref;

    const sessionRecord = {
      openedAt: session.openedAt,
      closedAt,
      durationSec
    };

    docRef.set(
      {
        userKey: user.key,
        userName: user.name || user['firstName'] || '',
        cardKey: session.cardKey,
        cardName: session.cardName || '',
        boardId: session.boardId || '',
        dayKey,
        totalSeconds: firebase.firestore.FieldValue.increment(durationSec),
        sessions: firebase.firestore.FieldValue.arrayUnion(sessionRecord),
        firstOpenedAt: session.openedAt,
        lastClosedAt: closedAt
      },
      { merge: true }
    ).catch(err => {
      console.error('[CardActivity] Failed to persist session', err);
    });
  }

  public getActivityForUserDay(userKey: string, dayKey: string): Observable<CardActivityEntity[]> {
    return this.afs
      .collection<CardActivityEntity>(this.dbPath, ref =>
        ref.where('userKey', '==', userKey).where('dayKey', '==', dayKey)
      )
      .valueChanges({ idField: 'key' })
      .pipe(
        map(rows =>
          (rows || []).slice().sort((a, b) => (b.lastClosedAt || 0) - (a.lastClosedAt || 0))
        )
      );
  }

  public dayKeyFromDate(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
  }

  private dayKeyFromTimestamp(ts: number): string {
    return this.dayKeyFromDate(new Date(ts));
  }
}
